import React, { useMemo } from 'react';
import {
  ReportField,
  ReportFlowPages,
  ReportFooter,
  ReportHeader,
  ReportMeta,
  ReportMetaRow,
  ReportNote,
  ReportTitleRow,
} from '../../../components/ui/Report';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import {
  COMPLIANCE_CODE_META,
  COMPLIANCE_META_BY_CODE,
  fmtComplianceHours,
  type WrhRulesThresholds,
} from '../complianceCodes';

export type ComplianceReportRow = {
  wrhDayId: number;
  workDate: string;
  crewName: string;
  rankName?: string | null;
  workHours: number;
  restHours: number;
  violationCodes: string[];
};

export type ComplianceReportData = {
  vesselId: number;
  vesselName?: string;
  vesselAbrv?: string | null;
  periodFrom: string;
  periodTo: string;
  rows: ComplianceReportRow[];
  ruleSet?: WrhRulesThresholds | null;
  managementCompany?: {
    id: number;
    name: string;
    logoStorageKey?: string | null;
  } | null;
};

type ComplianceReportProps = {
  data: ComplianceReportData;
  companyLogoSrc?: string | null;
};

function TableHead() {
  return (
    <table className="wrh-compliance-print-table wrh-compliance-print-table-head">
      <thead>
        <tr>
          <th className="wrh-comp-col-date">Date</th>
          <th className="wrh-comp-col-crew">Crew</th>
          <th className="wrh-comp-col-rank">Rank</th>
          <th className="wrh-comp-col-num">Work</th>
          <th className="wrh-comp-col-num">Rest</th>
          <th className="wrh-comp-col-issues">Issues</th>
        </tr>
      </thead>
    </table>
  );
}

function LineRow({
  row,
  rules,
}: {
  row: ComplianceReportRow;
  rules: WrhRulesThresholds | null;
}) {
  const codeLabel = (code: string) => COMPLIANCE_META_BY_CODE[code]?.short ?? code;
  return (
    <table className="wrh-compliance-print-table wrh-compliance-print-row-table">
      <tbody>
        <tr>
          <td className="wrh-comp-col-date nowrap">{formatDateGlobal(row.workDate)}</td>
          <td className="wrh-comp-col-crew">{row.crewName}</td>
          <td className="wrh-comp-col-rank">{row.rankName || '—'}</td>
          <td className="wrh-comp-col-num num">{fmtComplianceHours(row.workHours)}h</td>
          <td className="wrh-comp-col-num num">{fmtComplianceHours(row.restHours)}h</td>
          <td className="wrh-comp-col-issues">
            <div className="wrh-compliance-print-issues">
              {(row.violationCodes ?? []).map((c) => {
                const meta = COMPLIANCE_META_BY_CODE[c];
                return (
                  <span
                    key={c}
                    className={`wrh-compliance-print-chip tone-${meta?.tone ?? 'rose'}`}
                    title={meta ? `${meta.title}. ${meta.explain(rules)}` : c}
                  >
                    {codeLabel(c)}
                  </span>
                );
              })}
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  );
}

export const ComplianceReport: React.FC<ComplianceReportProps> = ({
  data,
  companyLogoSrc = null,
}) => {
  const rules = data.ruleSet ?? null;
  const rows = data.rows ?? [];

  const codeCounts = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of rows) {
      for (const c of r.violationCodes ?? []) {
        map.set(c, (map.get(c) ?? 0) + 1);
      }
    }
    return COMPLIANCE_CODE_META.map((m) => ({ ...m, count: map.get(m.code) ?? 0 })).filter(
      (m) => m.count > 0,
    );
  }, [rows]);

  const crewCount = useMemo(() => new Set(rows.map((r) => r.crewName)).size, [rows]);
  const vesselLabel = `${data.vesselName || '—'}${data.vesselAbrv ? ` (${data.vesselAbrv})` : ''}`;

  const firstHeader = useMemo(
    () => (
      <div className="wrh-compliance-print-flow-header">
        <ReportHeader>
          <ReportTitleRow
            title="Working / rest hours — non-conformities"
            formId="MLC / STCW"
            logoSrc={companyLogoSrc}
            logoAlt={data.managementCompany?.name || 'Management company logo'}
          />
          <ReportMeta>
            <ReportMetaRow>
              <ReportField label="Vessel" value={vesselLabel} />
              <ReportField
                label="Period"
                value={`${formatDateGlobal(data.periodFrom)} – ${formatDateGlobal(data.periodTo)}`}
              />
              <ReportField label="Rule set" value={rules?.name || '—'} />
            </ReportMetaRow>
            <ReportMetaRow>
              <ReportField label="Company" value={data.managementCompany?.name || '—'} />
              <ReportField label="NC rows" value={String(rows.length)} />
              <ReportField label="Crew with NC" value={String(crewCount)} />
            </ReportMetaRow>
          </ReportMeta>
          <p className="ui-report-legal">
            Lists recorded crew-days that fail the active WRH rule set. Hours are evaluated in sliding
            windows. Only non-conformities are shown.
          </p>
        </ReportHeader>

        {codeCounts.length > 0 ? (
          <div className="wrh-compliance-print-block">
            <h2 className="wrh-compliance-print-h">Breakdown by code</h2>
            <table className="wrh-compliance-print-summary">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Count</th>
                  <th>Meaning</th>
                </tr>
              </thead>
              <tbody>
                {codeCounts.map((m) => (
                  <tr key={m.code}>
                    <td>
                      <span className={`wrh-compliance-print-chip tone-${m.tone}`}>{m.short}</span>
                      <span className="wrh-compliance-print-code">{m.code}</span>
                    </td>
                    <td className="wrh-compliance-print-num">{m.count}</td>
                    <td>{m.title}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        <h2 className="wrh-compliance-print-h">Non-conformity list</h2>
        <TableHead />
      </div>
    ),
    [
      codeCounts,
      companyLogoSrc,
      crewCount,
      data.managementCompany?.name,
      data.periodFrom,
      data.periodTo,
      rows.length,
      rules?.name,
      vesselLabel,
    ],
  );

  const continueHeader = useMemo(
    () => (
      <div className="wrh-compliance-print-flow-header">
        <div className="wrh-compliance-print-continue-bar">
          <span className="wrh-compliance-print-continue-title">WRH non-conformities</span>
          <span className="wrh-compliance-print-continue-vessel">{vesselLabel}</span>
          <span className="wrh-compliance-print-continue-hint">continued</span>
        </div>
        <TableHead />
      </div>
    ),
    [vesselLabel],
  );

  const lastFooter = useMemo(
    () => (
      <div className="wrh-compliance-print-last">
        <h2 className="wrh-compliance-print-h">Code legend</h2>
        <table className="wrh-compliance-print-summary">
          <thead>
            <tr>
              <th>Code</th>
              <th>Rule</th>
            </tr>
          </thead>
          <tbody>
            {COMPLIANCE_CODE_META.map((m) => (
              <tr key={m.code}>
                <td>
                  <span className={`wrh-compliance-print-chip tone-${m.tone}`}>{m.short}</span>
                  <span className="wrh-compliance-print-code">{m.code}</span>
                </td>
                <td>
                  <strong>{m.title}</strong>
                  <div className="wrh-compliance-print-explain">{m.explain(rules)}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <ReportFooter>
          <ReportNote>
            Print date: {new Date().toLocaleString('en-GB')}. Thresholds from rule set
            {rules?.name ? ` “${rules.name}”` : ''}.
          </ReportNote>
        </ReportFooter>
      </div>
    ),
    [rules],
  );

  const { items, itemKeys } = useMemo(() => {
    if (rows.length === 0) {
      return {
        items: [
          <p key="empty" className="wrh-compliance-print-empty">
            No non-conformities in this period. All recorded crew-days meet the active WRH rules.
          </p>,
        ],
        itemKeys: ['empty'],
      };
    }
    return {
      items: rows.map((row) => (
        <LineRow key={`${row.wrhDayId}-${row.crewName}-${row.workDate}`} row={row} rules={rules} />
      )),
      itemKeys: rows.map((row) => `${row.wrhDayId}-${row.crewName}-${row.workDate}`),
    };
  }, [rows, rules]);

  return (
    <ReportFlowPages
      className="wrh-compliance-print"
      size="portrait"
      firstHeader={firstHeader}
      continueHeader={continueHeader}
      lastFooter={lastFooter}
      items={items}
      itemKeys={itemKeys}
    />
  );
};

export default ComplianceReport;
